import React from "react";
import { parseISO, addMinutes, format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../../../redux/store";
import { setCartItems } from "../../../../redux/cart/slice";
import { cartSelector } from "../../../../redux/cart/selectors";
import { fetchCreateCancelRent } from "../../../../redux/rent/asyncActions";

import { addCurrencyWord, setCartItemsToStorage, getPartnerIdFromStorage } from "../../../../utils";

import styles from "./CartIem.module.scss";

import { CartItemPropsType } from "./types";
import { IRacket, ISeat } from "../../types";

export const CartItem: React.FC<CartItemPropsType> = (props) => {
  const { itemId, rentType, rentDate, time, price, courtTitle, arenaTitle, isAttachedToCourt = false } = props;
  const { totalCartItems } = useSelector(cartSelector);

  const isRacket = rentType === "rackets";
  const dispatch = useAppDispatch();
  
  // Ракетки, привязанные к кортам, не должны иметь кнопку удаления
  const showDeleteButton = !isRacket || !isAttachedToCourt;

  // Получаем данные элемента
  const getCurrentItem = (): ISeat | IRacket | null => {
    const items = totalCartItems[rentType] || {};
    const item = Object.values(items).find((item: any) => item.id === itemId);
    return item as ISeat | IRacket | null;
  };

  const currentItem = getCurrentItem();
  const racketItem = isRacket ? (currentItem as IRacket) : null;
  const seatItem = !isRacket ? (currentItem as ISeat) : null;

  // Определяем тип ракетки (Padel или теннисная)
  const racketType = React.useMemo(() => {
    if (!isRacket || !racketItem) return null;
    if (racketItem.courtType === 'Padel') return 'padel';
    const title = racketItem.title?.toLowerCase() || '';
    const courtTitleLower = racketItem.courtTitle?.toLowerCase() || '';
    return (title.includes('padel') || courtTitleLower.includes('padel')) ? 'padel' : 'теннисная';
  }, [isRacket, racketItem]);

  const cartItemDateFormatted = React.useMemo(() => {
    return setDate(rentDate, time);
  }, [rentDate, time]);

  function setDate(date: string, startTime: string): string {
    try {
      const dateStr = `${date}T${startTime}:00`;
      const startDate = parseISO(dateStr);
      
      const dayOfWeek = format(startDate, 'EEEE', { locale: ru });
      const dayOfWeekCapitalized = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      
      const day = format(startDate, 'd');
      
      const month = format(startDate, 'MMMM', { locale: ru });
      
      const startTimeFormatted = format(startDate, 'HH:mm');
      
      const endDate = addMinutes(startDate, 60);
      const endTimeFormatted = format(endDate, 'HH:mm');
      
      return `${dayOfWeekCapitalized}, ${day} ${month}, ${startTimeFormatted} - ${endTimeFormatted}`;
    } catch (error) {
      console.error('Ошибка форматирования даты:', error);
      return `${date}, ${startTime}`;
    }
  }

  const handleDeleteCartItem = async () => {
    let cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));

    const cloneTotalCartItemsInType = JSON.parse(
      JSON.stringify(cloneTotalCartItems[rentType])
    );

    const filtredCloneTotalItemsInType = Object.keys(cloneTotalCartItemsInType)
      .filter((key) => cloneTotalCartItemsInType[key].id !== itemId)
      .reduce((res: { [key: string]: ISeat | IRacket }, key) => {
        res[key] = cloneTotalCartItemsInType[key];
        return res;
      }, {});

    const deletedItems = Object.keys(cloneTotalCartItemsInType)
      .filter((key) => cloneTotalCartItemsInType[key].id === itemId)
      .reduce((res: { [key: string]: ISeat | IRacket }, key) => {
        res[key] = cloneTotalCartItemsInType[key];
        return res;
      }, {});

    cloneTotalCartItems[rentType] = filtredCloneTotalItemsInType;
    
    if (rentType === "seats" && Object.keys(deletedItems).length > 0) {
      const deletedSeat = Object.values(deletedItems)[0] as ISeat;
      
      const rackets = cloneTotalCartItems.rackets || {};
      Object.keys(rackets).forEach((key) => {
        const racket = rackets[key] as IRacket;
        if (racket.courtId === deletedSeat.courtId && 
            racket.date === deletedSeat.date && 
            racket.time === deletedSeat.time) {
          delete rackets[key];
        }
      });
      cloneTotalCartItems.rackets = rackets;

      const partnerId = getPartnerIdFromStorage();
      
      if (partnerId) {
        try {
          const cancelBasket: { [key: string]: any } = {};
          const deletedKey = Object.keys(deletedItems)[0];
          cancelBasket[deletedKey] = deletedSeat;

          await dispatch(
            fetchCreateCancelRent({
              newBasket: {},
              cancelBasket: cancelBasket as any,
              partnerId,
            })
          ).unwrap();
        } catch (error) {
          console.error('Ошибка отмены бронирования корта:', error);
        }
      }
    }

    const totalDeletedItems =
      rentType === "seats"
        ? {
            delArr: {
              rackets: {},
              seats: { ...deletedItems },
            },
          }
        : {
            delArr: {
              rackets: { ...deletedItems },
              seats: {},
            },
          };

    setCartItemsToStorage(cloneTotalCartItems);
    dispatch(setCartItems(cloneTotalCartItems));
  };

  const handleIncrementRackets = () => {
    if (!racketItem || !racketItem.count) return;
    
    const maxCount = racketType === 'padel' ? 25 : 12;
    if (racketItem.count >= maxCount) return;

    let cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const rackets = cloneTotalCartItems.rackets || {};
    
    Object.keys(rackets).forEach((key) => {
      const racket = rackets[key] as IRacket;
      if (racket.id === itemId) {
        racket.count = (racket.count || 1) + 1;
      }
    });

    setCartItemsToStorage(cloneTotalCartItems);
    dispatch(setCartItems(cloneTotalCartItems));
  };

  const handleDecrementRackets = () => {
    if (!racketItem || !racketItem.count || racketItem.count <= 1) return;

    let cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const rackets = cloneTotalCartItems.rackets || {};
    
    Object.keys(rackets).forEach((key) => {
      const racket = rackets[key] as IRacket;
      if (racket.id === itemId) {
        racket.count = racket.count - 1;
      }
    });

    setCartItemsToStorage(cloneTotalCartItems);
    dispatch(setCartItems(cloneTotalCartItems));
  };

  const racketCount = racketItem?.count || 0;
  const racketPrice = racketItem ? (racketItem.price || 0) * racketCount : 0;
  const maxRacketCount = racketType === 'padel' ? 25 : 12;

  return (
    <div className={styles.wrapper}>
      <div className={styles.itemContainer}>
        <div className={styles.infoContainerLeft}>
          {isRacket ? (
            <>
              <p className={styles.courtName}>
                Ракетка {racketType ? `(${racketType})` : ''}
              </p>
              <p className={styles.date}>
                {cartItemDateFormatted}
              </p>
            </>
          ) : (
            <>
              <p className={styles.courtName}>
                {courtTitle?.replace(/Корт\s*/gi, '') || 'Неизвестный корт'}
              </p>
              <div>
                <p className={styles.corpus}>
                  {arenaTitle || ''}
                </p>
                <p className={styles.date}>
                  {cartItemDateFormatted}
                </p>
              </div>
            </>
          )}
        </div>

        <div className={isRacket ? styles.priceCountContainer : styles.priceCrossContainer}>
          {showDeleteButton ? (
            <button 
              className={styles.deleteButton} 
              onClick={handleDeleteCartItem}
              aria-label="Удалить"
            />
          ) : (
            <div className={styles.emptyIcon}  />
          )}
          
          {isRacket ? (
            <>
              <p className={styles.price}>
                {addCurrencyWord(racketPrice)}
              </p>
              <div className={styles.countControls}>
                <button
                  className={`${styles.countButton} ${styles.borderWidth}`}
                  onClick={handleDecrementRackets}
                  disabled={racketCount <= 1}
                >
                  <span className={styles.countButtonIcon}>-</span>
                </button>
                <div className={styles.countText}>
                  {racketCount}
                </div>
                <button
                  className={`${styles.countButton} ${styles.borderWidth}`}
                  onClick={handleIncrementRackets}
                  disabled={racketCount >= maxRacketCount}
                >
                  <span className={styles.countButtonIcon}>+</span>
                </button>
              </div>
            </>
          ) : (
            <p className={styles.price}>
              {addCurrencyWord(price)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
